import {
  useMemo,
  useRef,
  useState,
  type ChangeEventHandler,
  type TextareaHTMLAttributes,
} from "react";
import Prism from "prismjs";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-json";
import "prismjs/components/prism-markdown";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-typescript";
import { cn } from "@/utils";

export type HighlightLanguage = "javascript" | "json" | "markdown" | "sql" | "typescript";

interface HighlightedCodeEditorProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className" | "onChange"> {
  value?: string;
  defaultValue?: string;
  language: HighlightLanguage;
  minHeight?: number;
  invalid?: boolean;
  wrapMode?: "code" | "prose";
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
}

function escapeHtml(source: string) {
  return source
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function highlight(source: string, language: HighlightLanguage) {
  const grammar = Prism.languages[language];
  if (!grammar || !source) return escapeHtml(source);
  try {
    return Prism.highlight(source, grammar, language);
  } catch {
    return escapeHtml(source);
  }
}

export function HighlightedCodeEditor({
  value,
  defaultValue,
  language,
  minHeight = 132,
  invalid = false,
  wrapMode,
  onChange,
  rows = 6,
  ...textareaProps
}: HighlightedCodeEditorProps) {
  const preRef = useRef<HTMLPreElement>(null);
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value ?? "" : internalValue;
  const showHighlight = language !== "markdown";
  const resolvedWrapMode = wrapMode ?? "prose";
  const wraps = resolvedWrapMode === "prose";
  const highlightedHtml = useMemo(() => highlight(currentValue, language), [currentValue, language]);

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (event) => {
    if (!isControlled) setInternalValue(event.target.value);
    onChange?.(event);
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border bg-[var(--card)] shadow-xs transition-colors",
        invalid ? "border-[var(--destructive)]" : "border-[var(--border)] focus-within:border-[var(--ring)]",
      )}
    >
      {showHighlight && (
        <pre
          ref={preRef}
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 z-0 m-0 overflow-hidden px-3 py-2.5 font-mono text-sm leading-5",
            wraps ? "whitespace-pre-wrap break-words" : "min-w-max whitespace-pre",
          )}
          style={{ minHeight }}
        >
          <code
            className={`language-${language}`}
            dangerouslySetInnerHTML={{ __html: `${highlightedHtml || " "}\n` }}
          />
        </pre>
      )}
      <textarea
        {...textareaProps}
        aria-invalid={invalid || undefined}
        value={currentValue}
        onChange={handleChange}
        onScroll={(event) => {
          if (showHighlight && preRef.current) {
            preRef.current.style.transform = `translate(${-event.currentTarget.scrollLeft}px, ${-event.currentTarget.scrollTop}px)`;
          }
          textareaProps.onScroll?.(event);
        }}
        rows={rows}
        wrap={wraps ? "soft" : "off"}
        spellCheck={false}
        className={cn(
          "relative z-10 block w-full resize-y rounded-lg bg-transparent px-3 py-2.5 font-mono text-sm leading-5 caret-[var(--foreground)] placeholder:text-[var(--muted-foreground)] selection:bg-[var(--primary)]/20 focus:outline-none",
          wraps ? "overflow-y-auto overflow-x-hidden" : "overflow-auto whitespace-pre",
          showHighlight ? "text-transparent" : "text-[var(--foreground)]"
        )}
        style={{ minHeight }}
      />
    </div>
  );
}

export default HighlightedCodeEditor;
